import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { getClientes, createCliente, updateCliente, deleteCliente } from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Pencil, Trash2, UserCircle } from 'lucide-react';

const TIPOS_DOC = ['RUC', 'DNI', 'CE', 'OTRO'];

const ClientesPage = () => {
  const { empresaActual } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    tipo_doc: 'RUC', nro_doc: '', nombre_razon: '', telefono: '', email: '', direccion: '', activo: true
  });

  const loadData = useCallback(async () => {
    if (!empresaActual?.id) return;
    setLoading(true);
    try {
      const res = await getClientes(empresaActual.id);
      setItems(res.data);
    } catch (error) {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  const resetForm = () => {
    setFormData({ tipo_doc: 'RUC', nro_doc: '', nombre_razon: '', telefono: '', email: '', direccion: '', activo: true });
    setEditingItem(null);
  };

  const handleOpenDialog = (item = null) => {
    if (item) { setEditingItem(item); setFormData({ ...item }); }
    else { resetForm(); }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre_razon) { toast.error('El nombre es requerido'); return; }
    const payload = { ...formData, empresa_id: empresaActual.id };
    try {
      if (editingItem) { await updateCliente(editingItem.id, payload); toast.success('Cliente actualizado'); }
      else { await createCliente(payload); toast.success('Cliente creado'); }
      setDialogOpen(false); resetForm(); loadData();
    } catch (error) { toast.error('Error al guardar'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este cliente?')) return;
    try { await deleteCliente(id); toast.success('Cliente eliminado'); loadData(); }
    catch (error) { toast.error('Error al eliminar'); }
  };

  if (!empresaActual) return <div className="text-center py-12 text-gray-500">Selecciona una empresa</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <UserCircle className="w-8 h-8 text-emerald-600" />
          <h1 className="text-2xl font-bold text-gray-900">Clientes</h1>
        </div>
        <Button onClick={() => handleOpenDialog()}><Plus className="w-4 h-4 mr-2" />Nuevo Cliente</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div></div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No hay clientes</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo/Nro Doc</TableHead>
                  <TableHead>Nombre/Razón Social</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.tipo_doc}: {item.nro_doc || '-'}</TableCell>
                    <TableCell className="font-medium">{item.nombre_razon}</TableCell>
                    <TableCell>{item.telefono || '-'}</TableCell>
                    <TableCell>{item.email || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(item)}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingItem ? 'Editar' : 'Nuevo'} Cliente</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo Doc</Label>
                <Select value={formData.tipo_doc} onValueChange={(v) => setFormData({ ...formData, tipo_doc: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TIPOS_DOC.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Nro. Documento</Label><Input value={formData.nro_doc} onChange={(e) => setFormData({ ...formData, nro_doc: e.target.value })} /></div>
            </div>
            <div><Label>Nombre/Razón Social *</Label><Input value={formData.nombre_razon} onChange={(e) => setFormData({ ...formData, nombre_razon: e.target.value })} required /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Teléfono</Label><Input value={formData.telefono} onChange={(e) => setFormData({ ...formData, telefono: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientesPage;
